/**
 * Tool Manager — Cornerstone3D v2
 * Mikhmel Cornerstone4D DICOM Viewer Pro v5
 *
 * [AUDIT] v6.2 — MERGED: OOP Architecture + Full Diagnostics
 *
 * АРХИТЕКТУРА:
 * 1. Внутри — строгий класс ToolManager (ES2022 #private fields).
 * 2. Снаружи — 100% сохранение старого API (export function).
 * 3. Все 29 инструментов (включая TextAnnotate, Eraser, VolumeRotate, StackScrollMouseWheel).
 * 4. FIX #9 строго соблюден (нет авто-активации Crosshairs).
 *
 * ИСПРАВЛЕНИЯ:
 * 1. [CRITICAL] Принудительная установка touch-action:none на viewport-элементы
 * 2. [CRITICAL] Исправлена детекция touch-устройств (исключены десктопы с hover+fine pointer)
 * 3. [CRITICAL] Passive → Disabled для аннотаций (предотвращает перехват touch)
 * 4. [FIX] Event dispatch при смене инструмента (для синхронизации UI)
 * 5. [FIX] Диагностическое логирование во всех catch-блоках
 * 6. [FIX] Корректные touch-биндинги для всех режимов
 * 7. [FIX] WindowLevel с пустыми биндингами в 3D → setToolEnabled
 * 8. [FIX] Crosshairs защита — graceful degradation при < 2 viewports
 * 9. [FIX] Crosshairs НИКОГДА не авто-активируется. Только по явному нажатию (X).
 */

import { cornerstoneTools } from '../core/init.js';
// [FIX] Импорт из viewportState вместо viewport — разрыв циклической зависимости
import { getRenderingEngine as _getRE, cornerstone as _cornerstone, getViewportCount } from '../core/viewportState.js';
import { emit as busEmit, on as busOn, off as busOff, EVENTS } from '../core/eventBus.js';
import { log } from '../utils/logger.js';
// [11.1.35] Настоящий тост на экран. helpers → toastPolicy — оба без обратных
// зависимостей от tools, цикла нет (проверено по импортам обоих файлов).
import { showToast as _showToastReal } from '../utils/helpers.js';
import { ПРЯМОЙ_ОТВЕТ } from '../core/toastPolicy.js';

// ══════════════════════════════════════════════════════════════
// CORNERSTONE TOOLS IMPORTS
// ══════════════════════════════════════════════════════════════

const {
  // Annotation Tools
  LengthTool, ArrowAnnotateTool, BidirectionalTool, EllipticalROITool, RectangleROITool,
  CircleROITool, ProbeTool, AngleTool, CobbAngleTool, PlanarFreehandROITool,
  SplineROITool, LivewireContourTool, EraserTool,
  // Manipulation Tools
  WindowLevelTool, PanTool, ZoomTool, StackScrollTool, TrackballRotateTool,
  // Segmentation Tools
  BrushTool, PaintFillTool,
  // Display / Other Tools
  CrosshairsTool, ReferenceLinesTool, ScaleOverlayTool, MagnifyTool,
  OrientationMarkerTool, OverlayGridTool,
  // Enums & Utilities
  Enums: ToolEnums, ToolGroupManager, segmentation, annotation
} = cornerstoneTools;

// [FIX] Динамическое определение опциональных инструментов — не все версии cornerstoneTools их содержат
const TextAnnotateTool = cornerstoneTools.TextAnnotateTool || cornerstoneTools.TextAnnotationTool || null;
const VolumeRotateMouseWheelTool = cornerstoneTools.VolumeRotateMouseWheelTool || null;
const StackScrollMouseWheelTool = cornerstoneTools.StackScrollMouseWheelTool || null;
const MIPJumpToClickTool = cornerstoneTools.MIPJumpToClickTool || null;
const ThresholdBrushTool = cornerstoneTools.ThresholdBrushTool || null;
const CircleScissorsTool = cornerstoneTools.CircleScissorsTool || null;
const RectangleScissorsTool = cornerstoneTools.RectangleScissorsTool || null;
const SphereScissorsTool = cornerstoneTools.SphereScissorsTool || null;

// ══════════════════════════════════════════════════════════════
// CONSTANTS
// ══════════════════════════════════════════════════════════════

const RENDERING_ENGINE_ID = 'mikhmelRenderingEngine';
const TOOL_GROUP_ID = 'mikhmelToolGroup';
const TOOL_GROUP_3D_ID = 'mikhmelToolGroup3D';
// [6.7.33] Отдельная группа для СТЕК-окон (нативные срезы). См. комментарий
// в initTools: Crosshairs ломается, если в его группе есть стек-вьюпорт.
const TOOL_GROUP_STACK_ID = 'mikhmelToolGroupStack';

/**
 * Display overlay tools — these should be TOGGLED (Enabled/Disabled),
 * NOT set as the primary active tool with mouse bindings.
 * They render overlays and don't need mouse interaction.
 */
const DISPLAY_TOGGLE_TOOLS = new Set([
  'OverlayGrid', 'ScaleOverlay', 'OrientationMarker', 'ReferenceLines'
]);

/**
 * [11.1.35] НАДГРОБИЕ НЕМОГО ТОСТА. Здесь стояло `log.debug(...)` и НИЧЕГО
 * больше: «безопасное уведомление» писало в отладочный журнал, который
 * никто не видит. Все объяснения кисти из правки 11.1.26 — «Готовлю холст
 * сегментации…», «Кисть готова», «Кисти нужен объём…» — ни разу не дошли
 * до экрана. Владелец трижды жал кисть и трижды получал ТИШИНУ, хотя код
 * «говорил». Вывеска, которой нет, хуже немоты: мы думали, что объяснили.
 *
 * Опасение, из-за которого тост был выключен («viewerAPI proxy cascade
 * timeout»), к helpers.showToast не относится: это чистый DOM-тост без
 * прокси. Повод ПРЯМОЙ_ОТВЕТ — человек нажал кнопку инструмента, ответ на
 * нажатие заслон болтовни не глушит (правило toastPolicy).
 */
function showToastSafe(msg) {
  log.info('[Tools]', msg);
  try { _showToastReal(msg, 'info', 3500, ПРЯМОЙ_ОТВЕТ); }
  catch (e) { log.debug('[Tools] тост не показался:', e.message); }
}

// ══════════════════════════════════════════════════════════════
// TOOL MANAGER CLASS (Internal, #private state)
// ══════════════════════════════════════════════════════════════

/**
 * @class ToolManager
 *
 * Внутренний контроллер: вся мутация состояния через #private fields.
 * Наружу — только через export function обёртки (см. конец файла).
 * Гарантирует что никакой внешний код не может напрямую изменить
 * activeToolName, pendingTool, toolGroup и т.д.
 */
class ToolManager {
  #toolGroup2D = null;
  #toolGroupStack = null;
  #toolGroup3D = null;
  #activeToolName = null;
  #pendingTool = null;   // [FIX #9] Всегда null. Crosshairs не авто-активируется.
  #segReadyPending = false;   // [11.1.26] холст сегментации уже готовится — второй раз не начинаем
  #isTouchDevice = false;
  #registeredInGroup = new Set(); // [FIX #14] Tools actually added to the ToolGroup

  #config = {
    renderingEngineId: RENDERING_ENGINE_ID,
    fallbackTool: 'StackScroll'
  };

  /**
   * Полный реестр инструментов.
   *
   * minViewports — минимальное количество viewport'ов для активации.
   * Если viewport'ов меньше — используется fallbackTool (StackScroll).
   *
   * Вынесен в static для экономии памяти (один объект на весь модуль,
   * а не копия на каждый инстанс).
   */
  static REGISTRY = {
    // ── ANNOTATION TOOLS ──
    Length:            { tool: LengthTool,            category: 'annotation',    icon: '📏', shortcut: 'L' },
    ArrowAnnotate:    { tool: ArrowAnnotateTool,      category: 'annotation',    icon: '➡️', shortcut: 'A' },
    Bidirectional:    { tool: BidirectionalTool,      category: 'annotation',    icon: '↔️', shortcut: 'B' },
    EllipticalROI:    { tool: EllipticalROITool,      category: 'annotation',    icon: '⭕', shortcut: 'E' },
    RectangleROI:     { tool: RectangleROITool,       category: 'annotation',    icon: '▭', shortcut: 'R' },
    CircleROI:        { tool: CircleROITool,          category: 'annotation',    icon: '○', shortcut: 'C' },
    Probe:            { tool: ProbeTool,              category: 'annotation',    icon: '🎯', shortcut: 'P' },
    Angle:            { tool: AngleTool,              category: 'annotation',    icon: '📐', shortcut: 'G' },
    CobbAngle:        { tool: CobbAngleTool,          category: 'annotation',    icon: '🔀', shortcut: null },
    PlanarFreehandROI:{ tool: PlanarFreehandROITool,  category: 'annotation',    icon: '✏️', shortcut: 'F' },
    SplineROI:        { tool: SplineROITool,          category: 'annotation',    icon: '〰️', shortcut: null },
    LivewireContour:  { tool: LivewireContourTool,    category: 'annotation',    icon: '⚡', shortcut: null },
    TextAnnotate:     { tool: TextAnnotateTool,       category: 'annotation',    icon: '📝', shortcut: null },
    Eraser:           { tool: EraserTool,             category: 'annotation',    icon: '🧽', shortcut: null },

    // ── MANIPULATION TOOLS ──
    WindowLevel:      { tool: WindowLevelTool,        category: 'manipulation',  icon: '🌓', shortcut: 'W' },
    Pan:              { tool: PanTool,                category: 'manipulation',  icon: '✋', shortcut: 'H' },
    Zoom:             { tool: ZoomTool,               category: 'manipulation',  icon: '🔍', shortcut: 'Z' },
    StackScroll:      { tool: StackScrollTool,        category: 'manipulation',  icon: '📜', shortcut: 'S' },
    TrackballRotate:  { tool: TrackballRotateTool,    category: 'manipulation',  icon: '🌐', shortcut: 'T' },
    VolumeRotateMouseWheel:  { tool: VolumeRotateMouseWheelTool,  category: 'manipulation', icon: '🔄', shortcut: null },
    MIPJumpToClick:          { tool: MIPJumpToClickTool,         category: 'manipulation', icon: '👆', shortcut: null },
    StackScrollMouseWheel:   { tool: StackScrollMouseWheelTool,   category: 'manipulation', icon: '📜', shortcut: null },

    // ── SEGMENTATION TOOLS ──
    Brush:            { tool: BrushTool,              category: 'segmentation',  icon: '🖌️', shortcut: 'D' },
    ThresholdBrush:   { tool: ThresholdBrushTool,     category: 'segmentation',  icon: '🎨', shortcut: null },
    CircleScissors:   { tool: CircleScissorsTool,     category: 'segmentation',  icon: '✂️', shortcut: null },
    RectangleScissors:{ tool: RectangleScissorsTool,  category: 'segmentation',  icon: '✂️', shortcut: null },
    SphereScissors:   { tool: SphereScissorsTool,     category: 'segmentation',  icon: '✂️', shortcut: null },
    PaintFill:        { tool: PaintFillTool,          category: 'segmentation',  icon: '🪣', shortcut: null },

    // ── DISPLAY TOOLS (viewport constraints) ──
    Crosshairs:       { tool: CrosshairsTool,         category: 'display', icon: '➕', shortcut: 'X', minViewports: 2 },
    ReferenceLines:   { tool: ReferenceLinesTool,      category: 'display', icon: '┼', shortcut: null,  minViewports: 2 },
    ScaleOverlay:     { tool: ScaleOverlayTool,       category: 'display', icon: '📐', shortcut: null },
    Magnify:          { tool: MagnifyTool,            category: 'display', icon: '🔎', shortcut: 'M' },
    OrientationMarker:{ tool: OrientationMarkerTool,  category: 'display', icon: '🧭', shortcut: null },
    OverlayGrid:      { tool: OverlayGridTool,        category: 'display', icon: '📲', shortcut: null },
  };

  constructor() {
    this.#detectTouchDevice();
  }

  // ══════════════════════════════════════════════════════════════
  // EVENT BUS
  // ══════════════════════════════════════════════════════════════

  /**
   * Подписка на смену инструмента. Возвращает функцию отписки.
   * @param {function} callback — ({ toolName, previousTool, source }) => void
   * @returns {function} unsubscribe
   */
  onToolChange(callback) {
    busOn(EVENTS.TOOL_CHANGED, callback);
    return () => busOff(EVENTS.TOOL_CHANGED, callback);
  }

  /** [FIX #4] Оповещение UI и мобильного toolbar при смене инструмента */
  #emitToolChange(toolName, previousTool, source = 'user') {
    busEmit(EVENTS.TOOL_CHANGED, { toolName, previousTool, source });
  }

  // ══════════════════════════════════════════════════════════════
  // INITIALIZATION
  // ══════════════════════════════════════════════════════════════

  /**
   * Инициализация инструментов для заданных viewport'ов.
   * Создаёт 2D и 3D tool groups, регистрирует все инструменты,
   * устанавливает безопасные стартовые биндинги (Pan на Primary).
   *
   * @param {string[]} viewportIds — ID всех viewport'ов
   * @param {string} renderingEngineId
   * @returns {ToolGroup} 2D tool group
   */
  initTools(viewportIds, renderingEngineId = RENDERING_ENGINE_ID) {
    this.#config.renderingEngineId = renderingEngineId;
    this.#pendingTool = null; // [FIX #9] Сброс отложенного инструмента

    // [FIX #2] Кэшируем детекцию touch-устройства один раз
    this.#detectTouchDevice();

    // [FIX #1] Блокировка нативного скролла/зума браузером
    this.#applyTouchActionCSS(viewportIds);

    // ────────────────────────────────────────────────────────────
    // [FIX #14] Monkey-patch display tools BEFORE registration.
    // Cornerstone's filterToolsWithMoveableHandles iterates ALL
    // Enabled/Active tools and calls getHandleNearImagePoint().
    // Display overlay tools (Grid, Scale, Orientation, ReferenceLines)
    // don't implement this method → "is not a function" crash.
    // We add a safe no-op so Cornerstone can iterate them safely.
    // ────────────────────────────────────────────────────────────
    const displayToolClasses = [
      OverlayGridTool, ScaleOverlayTool, OrientationMarkerTool, ReferenceLinesTool
    ];
    for (const ToolClass of displayToolClasses) {
      if (!ToolClass) continue;
      if (!ToolClass.prototype.getHandleNearImagePoint) {
        ToolClass.prototype.getHandleNearImagePoint = () => undefined;
      }
      if (!ToolClass.prototype.handleSelectedCallback) {
        ToolClass.prototype.handleSelectedCallback = () => {};
      }
      if (!ToolClass.prototype.toolSelectedCallback) {
        ToolClass.prototype.toolSelectedCallback = () => {};
      }
      if (!ToolClass.prototype.isPointNearTool) {
        ToolClass.prototype.isPointNearTool = () => false;
      }
    }

    // [FIX #15] OverlayGridTool crashes accessing sourceImageIds[0].
    // Comprehensive fix: ensure sourceImageIds always exists on any instance.
    if (OverlayGridTool) {
      // 1. Patch default configuration at class level
      if (OverlayGridTool.defaultConfiguration) {
        if (!OverlayGridTool.defaultConfiguration.sourceImageIds) {
          OverlayGridTool.defaultConfiguration.sourceImageIds = [];
        }
      }
      // 2. Patch prototype configuration
      if (OverlayGridTool.prototype) {
        const origConstructor = OverlayGridTool.prototype.constructor;
        // Wrap constructor to inject sourceImageIds
        if (!OverlayGridTool._gridPatched) {
          const origOnSetToolEnabled = OverlayGridTool.prototype.onSetToolEnabled;
          const origInit = OverlayGridTool.prototype._init;
          
          // Patch _init if exists
          if (typeof origInit === 'function') {
            OverlayGridTool.prototype._init = function(...args) {
              if (!this.configuration) this.configuration = {};
              if (!this.configuration.sourceImageIds) {
                this.configuration.sourceImageIds = this._getImageIdsFromViewports() || [];
              }
              try { return origInit.apply(this, args); } catch(e) { /* silently skip init error */ }
            };
          }
          // Patch onSetToolEnabled if exists
          if (typeof origOnSetToolEnabled === 'function') {
            OverlayGridTool.prototype.onSetToolEnabled = function(...args) {
              if (!this.configuration) this.configuration = {};
              if (!this.configuration.sourceImageIds) {
                this.configuration.sourceImageIds = this._getImageIdsFromViewports() || [];
              }
              try { return origOnSetToolEnabled.apply(this, args); } catch(e) { /* silently skip */ }
            };
          }
          // Helper to get imageIds from viewports or volume cache
          OverlayGridTool.prototype._getImageIdsFromViewports = function() {
            try {
              const re = _getRE();
              if (!re) return null;
              for (const vp of re.getViewports()) {
                try { const ids = vp.getImageIds?.(); if (ids?.length) return ids; } catch(e) {}
                try {
                  const actors = vp.getActors?.();
                  if (actors?.length) {
                    const vid = actors[0]?.uid || actors[0]?.referenceId;
                    if (vid) {
                      const vol = _cornerstone?.cache?.getVolume?.(vid);
                      if (vol?.imageIds?.length) return vol.imageIds;
                    }
                  }
                } catch(e) {}
              }
            } catch(e) {}
            return null;
          };
          OverlayGridTool._gridPatched = true;
        }
      }
    }

    // 1. Глобальная регистрация инструментов
    const registeredTools = new Set();
    const failedTools = new Set();
    for (const [name, entry] of Object.entries(ToolManager.REGISTRY)) {
      if (!entry.tool) {
        failedTools.add(name);
        continue;
      }
      // [FIX] Verify tool class is valid (has toolName property or prototype)
      // Broken tool classes in CS 4.17.4 exist as objects but lack proper structure
      const cls = entry.tool;
      if (typeof cls !== 'function' || !cls.toolName) {
        log.debug(`[Tools] Skipping ${name}: invalid tool class (typeof=${typeof cls}, toolName=${cls?.toolName})`);
        failedTools.add(name);
        continue;
      }
      try {
        cornerstoneTools.addTool(cls);
        registeredTools.add(name);
      } catch (e) {
        if (e.message?.includes('already')) {
          registeredTools.add(name);
        } else {
          failedTools.add(name);
        }
      }
    }

    // 2. Разделяем viewport'ы для 2D и 3D
    const is3D = (id) => id === 'volume3d' || id === 'view-3d';
    const vpIds3D = viewportIds.filter(id => is3D(id));
    const vpIdsNon3D = viewportIds.filter(id => !is3D(id));

    // ══════════════════════════════════════════════════════════════
    // [6.7.33 FIX] СТЕК-ОКНА — В ОТДЕЛЬНУЮ ГРУППУ
    //
    // Проблема. Окно «Нативные срезы» имеет тип STACK и раньше попадало
    // в ту же группу, что и три ортографических окна MPR. Инструмент
    // Crosshairs в Cornerstone3D рассчитан на объёмные окна: он обходит
    // все вьюпорты группы и для каждого считает геометрию среза. Наткнувшись
    // на стек, обход обрывается — линии успевают отрисоваться только в первом
    // окне, а в остальных не появляются вовсе. Cornerstone при этом не бросает
    // исключение, только пишет в консоль, поэтому ошибка выглядела как
    // «перекрестия просто не работают».
    //
    // Решение. Ортографические окна остаются в основной группе (там живёт
    // Crosshairs), стек-окна выносятся в свою. Если ортографических окон
    // меньше двух (обычные раскладки 1×1, 1×2, 2×2 — там всё стеки),
    // поведение остаётся прежним: всё в одной группе, Crosshairs всё равно
    // не активируется из-за требования minViewports.
    // ══════════════════════════════════════════════════════════════
    const _typeOf = (id) => {
      try { return String(_getRE()?.getViewport?.(id)?.type || ''); } catch (e) { return ''; }
    };
    const isOrtho = (id) => _typeOf(id).toLowerCase().includes('orthographic');
    const orthoIds = vpIdsNon3D.filter(isOrtho);
    const stackIds = vpIdsNon3D.filter(id => !isOrtho(id));
    const splitStack = orthoIds.length >= 2 && stackIds.length > 0;

    const vpIds2D = splitStack ? orthoIds : vpIdsNon3D;
    const vpIdsStack = splitStack ? stackIds : [];
    if (splitStack) {
      log.info(`[Tools] Стек-окна вынесены в отдельную группу: [${stackIds.join(', ')}]. ` +
               `Crosshairs работает с MPR-окнами: [${orthoIds.join(', ')}]`);
    }

    // --- 2D/MPR tool group ---
    try { ToolGroupManager.destroyToolGroup(TOOL_GROUP_ID); }
    catch (e) { log.debug('[Tools] No existing 2D group to destroy'); }

    this.#toolGroup2D = ToolGroupManager.createToolGroup(TOOL_GROUP_ID);
    for (const vpId of vpIds2D) {
      this.#toolGroup2D.addViewport(vpId, renderingEngineId);
    }

    // [FIX] Add tools to 2D group with console.warn interception.
    // ToolGroup.addTool() does NOT throw on failure — it console.warns.
    // We intercept console.warn to detect "not registered" and skip those.
    // [11.1.37] ПРИЧИНЫ ГАШЕНИЯ — НА СВЕТ. «Заливка» и ножницы приходили к
    // врачу серыми, а ПОЧЕМУ — знал только log.debug, которого никто не
    // видит: перехваченный warn глотался, исключение конструктора глоталось.
    // Три круга по кисти уже показали цену немой диагностики. Теперь причина
    // каждого отказа запоминается и уходит в журнал уровнем warn одной
    // строкой, а упавшему на исключении инструменту даётся ВТОРАЯ попытка
    // после повторной глобальной регистрации (это дёшево и безвредно:
    // addTool в v5 идемпотентен).
    this.#registeredInGroup = new Set();
    const причиныОтказа = new Map();
    const _origWarn = console.warn;
    const _добавитьВГруппу = (name) => {
      if (name === 'OverlayGrid') {
        this.#toolGroup2D.addTool(name, { configuration: { sourceImageIds: [] } });
      } else {
        this.#toolGroup2D.addTool(name);
      }
    };
    for (const name of registeredTools) {
      let addFailed = false;
      console.warn = (...args) => {
        const msg = args.join(' ');
        if (msg.includes('not registered') || msg.includes('not added')) {
          addFailed = true;
          причиныОтказа.set(name, msg);
          return;   // warn проглочен намеренно — он уедет в сводку ниже
        }
        _origWarn.apply(console, args);
      };
      try {
        _добавитьВГруппу(name);
        if (!addFailed) {
          this.#registeredInGroup.add(name);
        } else {
          failedTools.add(name);
        }
      } catch (e) {
        // Конструктор инструмента упал — вторая попытка после повторной
        // глобальной регистрации класса.
        причиныОтказа.set(name, 'исключение: ' + e.message);
        try {
          const cls = ToolManager.REGISTRY[name]?.tool;
          if (cls) cornerstoneTools.addTool(cls);
          addFailed = false;
          _добавитьВГруппу(name);
          if (!addFailed) {
            this.#registeredInGroup.add(name);
            причиныОтказа.set(name, 'ожил со второй попытки: ' + e.message);
          } else {
            failedTools.add(name);
          }
        } catch (e2) {
          причиныОтказа.set(name, `исключение: ${e.message}; вторая попытка: ${e2.message}`);
          failedTools.add(name);
        }
      }
      console.warn = _origWarn;
    }

    // Безопасные стартовые биндинги (Pan на Primary до загрузки данных)
    this.#setDefaultBindings();

    // ── [X] Перекрестья: раскраска линий по окнам (конфиг после регистрации в 2D-группе) ──
    // [6.0.2 FIX] Блок вынесен ИЗ ветки if(vpIds3D): после замены 3D-квадранта на
    // нативное окно ветка перестала выполняться, и конфиг терялся.
    if (registeredTools.has('Crosshairs') && this.#toolGroup2D) {
      try {
        const XCOLORS = { axial: 'rgb(235, 90, 90)', sagittal: 'rgb(235, 205, 80)', coronal: 'rgb(95, 205, 130)' };
        this.#toolGroup2D.setToolConfiguration('Crosshairs', {
          getReferenceLineColor: (vpId) => XCOLORS[vpId] || 'rgb(160, 160, 160)',
          getReferenceLineControllable: () => true,
          getReferenceLineDraggableRotatable: () => true,
          getReferenceLineSlabThicknessControlsOn: () => false,
        });
        log.debug('[Tools] Crosshairs configured (цвета axial/sagittal/coronal)');
      } catch (e) { log.debug('[Tools] Crosshairs config:', e.message); }
    }

    // [Зум] Масштабирование строго от центра окна (как у станционного вьювера).
    // [6.0.2 FIX] Тоже вынесен из ветки if(vpIds3D) — без этого Zoom по умолчанию
    // «тянул» изображение к курсору, и срезы теряли централизацию.
    if (registeredTools.has('Zoom') && this.#toolGroup2D) {
      try { this.#toolGroup2D.setToolConfiguration('Zoom', { zoomToCenter: true }); }
      catch (e) { log.debug('[Tools] zoomToCenter:', e.message); }
    }

    // --- 3D tool group ---
    if (vpIds3D.length > 0) {
      try { ToolGroupManager.destroyToolGroup(TOOL_GROUP_3D_ID); }
      catch (e) { log.debug('[Tools] No existing 3D group to destroy'); }

      this.#toolGroup3D = ToolGroupManager.createToolGroup(TOOL_GROUP_3D_ID);
      for (const vpId of vpIds3D) {
        this.#toolGroup3D.addViewport(vpId, renderingEngineId);
      }
      for (const name of registeredTools) {
        // Only add tools that successfully registered in 2D group
        if (!this.#registeredInGroup.has(name)) continue;
        try { this.#toolGroup3D.addTool(name); }
        catch (e) { log.debug(`[Tools] 3D group skip ${name}:`, e.message); }
      }

      this.#applyToolState(this.#toolGroup3D, 'TrackballRotate');
      log.debug(`[Tools] 3D group initialized with ${vpIds3D.length} viewport(s)`);
    }

    // --- Группа для стек-окон (нативные срезы) ---
    this.#toolGroupStack = null;
    if (vpIdsStack.length > 0) {
      try { ToolGroupManager.destroyToolGroup(TOOL_GROUP_STACK_ID); }
      catch (e) { log.debug('[Tools] No existing stack group to destroy'); }
      try {
        this.#toolGroupStack = ToolGroupManager.createToolGroup(TOOL_GROUP_STACK_ID);
        for (const vpId of vpIdsStack) {
          this.#toolGroupStack.addViewport(vpId, renderingEngineId);
        }
        for (const name of registeredTools) {
          if (!this.#registeredInGroup.has(name)) continue;
          // Crosshairs и перекрёстные линии в стек-окне не имеют смысла
          if (name === 'Crosshairs' || name === 'ReferenceLines' || name === 'OverlayGrid') continue;
          try { this.#toolGroupStack.addTool(name); }
          catch (e) { log.debug(`[Tools] stack group skip ${name}:`, e.message); }
        }
        this.#applyToolState(this.#toolGroupStack, 'StackScroll');
        log.debug(`[Tools] Stack group initialized with ${vpIdsStack.length} viewport(s)`);
      } catch (e) {
        log.warn('[Tools] Не удалось создать группу стек-окон:', e.message);
        this.#toolGroupStack = null;
      }
    }

    // [FIX #1] Повторная установка touch-action после создания tool group
    // (Cornerstone может пересоздать canvas при добавлении viewport)
    requestAnimationFrame(() => this.#applyTouchActionCSS(viewportIds));

    // Compute final set of failed tools — anything in REGISTRY but NOT in registeredInGroup
    for (const name of Object.keys(ToolManager.REGISTRY)) {
      if (!this.#registeredInGroup.has(name)) failedTools.add(name);
    }
    log.debug(`[Tools] Registered: [${[...this.#registeredInGroup].join(', ')}]`);
    if (failedTools.size > 0) {
      // [11.1.37] Warn, не debug: по этой строке врач присылает виновника.
      const сводка = [...failedTools]
        .map((n) => `${n} — ${причиныОтказа.get(n) || 'класс отсутствует в сборке библиотеки'}`)
        .join('; ');
      log.warn(`[Tools] Кнопки погашены (${failedTools.size}): ${сводка}`);
    }

    // [FIX] Скрыть/деактивировать кнопки инструментов, которые не зарегистрировались
    if (failedTools.size > 0) {
      for (const name of failedTools) {
        // Desktop buttons
        const btns = document.querySelectorAll(`.tool-btn[data-tool="${name}"]`);
        btns.forEach(btn => {
          btn.style.opacity = '0.3';
          btn.style.pointerEvents = 'none';
          btn.title = `${btn.title || name} (недоступен в этой версии)`;
        });
        // Mobile buttons
        const mBtns = document.querySelectorAll(`.m-tool-btn[data-tool="${name}"]`);
        mBtns.forEach(btn => {
          btn.style.opacity = '0.3';
          btn.style.pointerEvents = 'none';
        });
      }
      log.debug(`[Tools] Disabled ${failedTools.size} unavailable tool buttons: ${[...failedTools].join(', ')}`);
    }

    return this.#toolGroup2D;
  }

  /**
   * Стартовые безопасные биндинги до загрузки пикселей.
   * Pan на Left Mouse + 1 палец, Zoom на Right Mouse + 2 пальца.
   */
  #setDefaultBindings() {
    const { MouseBindings } = ToolEnums;
    try {
      this.#toolGroup2D.setToolActive('Pan', {
        bindings: [
          { mouseButton: MouseBindings.Primary },
          { mouseButton: MouseBindings.Auxiliary },
          { numTouchPointers: 1 }
        ],
      });
      this.#toolGroup2D.setToolActive('Zoom', {
        bindings: [
          { mouseButton: MouseBindings.Secondary },
          { numTouchPointers: 2 }
        ],
      });
    } catch (e) {
      log.warn('[Tools] setDefaultBindings failed:', e.message);
    }
    this.#activeToolName = 'Pan';
  }

  // ══════════════════════════════════════════════════════════════
  // CORE: TOOL STATE MANAGEMENT
  // ══════════════════════════════════════════════════════════════

  /**
   * Активация инструментов для работы с данными (после загрузки серии).
   * Устанавливает StackScroll как основной инструмент.
   * Вызывается из viewport.js после loadStack/loadVolume.
   */
  activateDataTools() {
    this.#pendingTool = null; // [FIX #9] StackScroll всегда дефолт после загрузки

    const defaultMainTool = 'StackScroll';

    // --- 2D group ---
    if (this.#toolGroup2D) {
      log.debug(`[Tools] activateDataTools: isTouch=${this.#isTouchDevice}, default=${defaultMainTool}`);
      this.#applyToolState(this.#toolGroup2D, defaultMainTool);
      const prev = this.#activeToolName;
      this.#activeToolName = defaultMainTool;
      this.#emitToolChange(defaultMainTool, prev, 'auto');
    }

    // --- 3D group ---
    if (this.#toolGroup3D) {
      try {
        this.#applyToolState(this.#toolGroup3D, 'TrackballRotate');
        // [FIX #7] В 3D WindowLevel → Enabled (рендерит оверлей, не реагирует на mouse/touch)
        this.#toolGroup3D.setToolEnabled('WindowLevel');
      } catch (e) {
        log.warn('[Tools] 3D activateDataTools failed:', e.message);
      }
    }

    // backwards compatibility
    window._mobileActiveTool = defaultMainTool;
  }

  /**
   * Основная функция переключения инструментов из UI.
   * [FIX #4] Эмитит событие 'toolchange' для синхронизации мобильного UI.
   * [FIX #8] Graceful degradation: если инструмент требует N viewports, fallback.
   * [FIX #9] НЕ сохраняет в pendingTool — Crosshairs не авто-активируется.
   * [FIX #11] Display overlay tools (Grid, Scale, ReferenceLines, OrientationMarker)
   *           are toggled Enabled/Disabled instead of becoming the primary tool.
   * [FIX #12] Segmentation tools auto-create a labelmap if none exists.
   *
   * @param {string} toolName — имя инструмента из REGISTRY
   * @returns {boolean} успех
   */
  setActiveTool(toolName) {
    if (!ToolManager.REGISTRY[toolName]) {
      log.warn(`[Tools] setActiveTool: "${toolName}" not found in registry.`);
      return false;
    }

    // [FIX #14] Check if tool was actually registered in the toolGroup.
    // Scissors tools (CircleScissors, RectangleScissors, SphereScissors) and
    // MIPJumpToClick don't exist in Cornerstone 4.17.4 — their imports are null.
    if (!this.#registeredInGroup.has(toolName)) {
      log.warn(`[Tools] "${toolName}" is not available in this Cornerstone version`);
      return false;
    }

    // [X] Перекрестья: повторный клик — полностью выключить и вернуть прокрутку
    if (toolName === 'Crosshairs' && this.#activeToolName === 'Crosshairs') {
      return this.setActiveTool('StackScroll');
    }
    // [X] Перекрестьям нужно минимум два окна (раскладка MPR)
    if (toolName === 'Crosshairs') {
      const nVp = (this.#toolGroup2D?.getViewportIds?.() || []).length;
      if (nVp < 2) {
        showToastSafe('✚ Перекрестья работают в раскладке MPR (минимум два окна)');
        return false;
      }
    }

    // [FIX #11] Display overlay tools → toggle, do NOT change primary tool
    if (DISPLAY_TOGGLE_TOOLS.has(toolName)) {
      return this.toggleDisplayTool(toolName);
    }

    // [FIX #12] Segmentation tools require an active segmentation.
    // If none exists, block activation and show a message.
    if (ToolManager.REGISTRY[toolName]?.category === 'segmentation') {
      if (!this.#segmentationCreated) {
        // [11.1.26] БАГ ВЛАДЕЛЬЦА: «кисть не работает ни на КТ, ни на МРТ, ни
        // в одной проекции — левая кнопка продолжает вращать/листать».
        // Причина ровно здесь: холст сегментации (labelmap) создаётся
        // АСИНХРОННО, а эта ветка возвращала false МОЛЧА — инструмент не
        // менялся, и мышь оставалась на прокрутке. Комментарий «user needs to
        // retry» описывал требование к человеку, которого никто не
        // предупредил: ни тоста, ни повторной попытки. Вдобавок при stack-
        // раскладке (одиночное окно без тома) создание тихо пропускалось —
        // отсюда «ни в одном режиме».
        //
        // Теперь: говорим, что готовим холст, и САМИ включаем инструмент,
        // когда он готов. Если тома нет вовсе — объясняем словами, что
        // делать (перейти в MPR/3D), вместо тишины.
        if (this.#segReadyPending) return false;   // уже готовим — не плодим холсты
        this.#segReadyPending = true;
        showToastSafe('🖌 Готовлю холст сегментации…');
        this.#tryCreateSegmentation(toolName)
          .then((готово) => {
            this.#segReadyPending = false;
            if (готово) {
              const вышло = this.setActiveTool(toolName);
              if (!вышло) showToastSafe('🖌 Холст готов, но инструмент не включился — нажмите кисть ещё раз');
              // Успех объявляет сам setActiveTool (окна названы и подсвечены) —
              // второй тост здесь дублировал бы первый.
            } else {
              // [11.1.35] «…или 3D» УБРАНО из подсказки: в раскладке из одного
              // объёмного окна холсту лечь некуда — кисть рисует в срезовых.
              showToastSafe('🖌 Кисти нужны срезовые окна: включите раскладку MPR и повторите');
            }
          })
          .catch(() => {
            this.#segReadyPending = false;
            showToastSafe('🖌 Не удалось подготовить холст сегментации');
          });
        return false;
      }
    }

    let actualTool = toolName;
    const previousTool = this.#activeToolName;

    // [FIX #8] Проверка viewport requirement (Graceful Degradation)
    if (this.#toolGroup2D) {
      const fallback = this.#checkViewportRequirement(toolName, this.#toolGroup2D);
      if (fallback) {
        log.warn(`[Tools] ⚠️ "${toolName}" requires more viewports, using "${fallback}" instead`);
        actualTool = fallback;
      }
    }

    let success = false;

    log.debug(`[Tools] setActiveTool: "${previousTool}" → "${actualTool}"`);

    // [6.7.33] Стек-окна живут в своей группе — им тоже нужен инструмент,
    // иначе нативное окно перестанет слушаться мыши. Crosshairs и перекрёстные
    // линии туда не передаём: они там неприменимы.
    if (this.#toolGroupStack) {
      const stackTool = (actualTool === 'Crosshairs') ? 'StackScroll' : actualTool;
      try { this.#applyToolState(this.#toolGroupStack, stackTool); }
      catch (e) { log.debug('[Tools] stack group apply:', e.message); }
    }

    // 1. Применяем к 2D группе
    if (this.#toolGroup2D) {
      this.#applyToolState(this.#toolGroup2D, actualTool);
      success = true;
    }

    // 2. Применяем к 3D группе (только допустимые инструменты)
    if (this.#toolGroup3D) {
      const valid3DTools = ['TrackballRotate', 'Pan', 'Zoom', 'WindowLevel']; // [X] Crosshairs — только MPR-группа
      this.#applyToolState(
        this.#toolGroup3D,
        valid3DTools.includes(actualTool) ? actualTool : 'TrackballRotate'
      );
      success = true;
    }

    if (success) {
      // [X] Вне активности перекрестья гасим целиком: passive оставил бы линии на экране
      if (previousTool === 'Crosshairs' && actualTool !== 'Crosshairs') {
        try { this.#toolGroup2D?.setToolDisabled('Crosshairs'); } catch (e) { log.debug('[Tools]', e.message); }
      }
      this.#activeToolName = actualTool;
      window._mobileActiveTool = actualTool;
      this.#emitToolChange(actualTool, previousTool, 'user');
      // [11.1.35] Кисть ВСЛУХ называет, где она рисует, и подсвечивает эти
      // окна. Симптом «левая кнопка вращает объём» — это объёмное 3D-окно,
      // где на левой кнопке ВСЕГДА вращение (так устроено, см. #applyToolState
      // п.2): кисть там не рисует ни при каком коде. Раньше об этом молчали,
      // и любая проба кисти в 3D-окне выглядела как поломка.
      this.#объявитьОкнаКисти(actualTool);
    }

    return success;
  }

  /** [11.1.35] Слушатели-подсказки на 3D-окнах при активной кисти — их уборка. */
  #подсказка3DУборка = [];
  /** Подсказка в 3D-окне говорится один раз на включение кисти, не на каждый клик. */
  #подсказка3DСказана = false;

  /**
   * [11.1.35] При включении инструмента сегментации: подсветить срезовые
   * окна (там кисть рисует), сказать это словами и повесить на объёмные окна
   * разовую подсказку «здесь вращение». При любом другом инструменте —
   * снять подсказки. Подсветка и слушатели НЕ владеют окнами: они ничего не
   * перехватывают (без preventDefault) — хозяин мыши остаётся прежним.
   */
  #объявитьОкнаКисти(toolName) {
    for (const снять of this.#подсказка3DУборка.splice(0)) {
      try { снять(); } catch (e) { /* слушатель уже мёртв */ }
    }
    this.#подсказка3DСказана = false;
    if (ToolManager.REGISTRY[toolName]?.category !== 'segmentation') return;
    try {
      const re = _getRE();
      const окна = re?.getViewports?.() || [];
      const срезовые = [];
      for (const vp of окна) {
        if (vp?.type === 'orthographic' && vp.element) {
          срезовые.push(vp.id);
          const эл = vp.element;
          // [11.1.38] Запоминаем, В КАКОМ окне врач ведёт кисть: от этого
          // зависит ось, вдоль которой замкнут его контур, а значит и ось
          // заливки («Залить контуры»). Заливать аксиальные кольца по
          // сагиттальной оси бессмысленно — там они тонкие линии.
          if (!эл.__mikhmelЗапомненоОкно) {
            эл.__mikhmelЗапомненоОкно = true;
            эл.addEventListener('pointerdown', () => { try { window._mikhmelПоследнееОкно = vp.id; } catch (e) { /* */ } });
          }
          const было = эл.style.outline;
          эл.style.outline = '2px solid rgba(79,140,255,0.9)';
          эл.style.outlineOffset = '-2px';
          setTimeout(() => { try { эл.style.outline = было || ''; } catch (e) { /* */ } }, 2200);
        } else if (String(vp?.type || '').includes('volume3d') && vp.element) {
          const эл = vp.element;
          const подскажи = () => {
            if (this.#подсказка3DСказана) return;
            this.#подсказка3DСказана = true;
            showToastSafe('🖌 В объёмном 3D-окне кисть не рисует — здесь левая кнопка вращает. Рисуйте в срезовых окнах (они подсвечены при включении кисти)');
          };
          эл.addEventListener('pointerdown', подскажи);
          this.#подсказка3DУборка.push(() => эл.removeEventListener('pointerdown', подскажи));
        }
      }
      if (срезовые.length) {
        showToastSafe(`🖌 Кисть готова — рисуйте левой кнопкой в срезовых окнах (${срезовые.join(', ')})`);
      }
    } catch (e) {
      log.debug('[Tools] объявление окон кисти:', e.message);
    }
  }

  // ══════════════════════════════════════════════════════════════
  // [FIX #11] DISPLAY OVERLAY TOOLS — TOGGLE LOGIC
  // ══════════════════════════════════════════════════════════════

  /** Internal set of currently enabled display overlay tools */
  #enabledDisplayTools = new Set();

  /**
   * Toggle a display overlay tool (OverlayGrid, ScaleOverlay, ReferenceLines, OrientationMarker).
   * These tools render overlays and don't need mouse bindings.
   * They are toggled independently of the primary active tool.
   *
   * @param {string} toolName
   * @returns {boolean} — true if now enabled, false if disabled
   */
  toggleDisplayTool(toolName) {
    const group = this.#toolGroup2D;
    if (!group) {
      log.warn(`[Tools] toggleDisplayTool: no 2D tool group`);
      return false;
    }

    // [FIX #14] Check tool is actually in the group
    if (!this.#registeredInGroup.has(toolName)) {
      log.warn(`[Tools] toggleDisplayTool: "${toolName}" not in toolGroup`);
      return false;
    }

    const wasEnabled = this.#enabledDisplayTools.has(toolName);

    try {
      if (wasEnabled) {
        group.setToolDisabled(toolName);
        this.#enabledDisplayTools.delete(toolName);
        log.debug(`[Tools] Display tool DISABLED: ${toolName}`);
      } else {
        // [FIX] OverlayGrid: must set sourceImageIds on tool instance BEFORE setToolEnabled,
        // because setToolEnabled triggers _init() which accesses sourceImageIds[0]
        if (toolName === 'OverlayGrid') {
          // Get imageIds from volume cache
          const imageIds = this.#getOverlayGridImageIds();
          if (!imageIds?.length) {
            console.log('[Tools] OverlayGrid: no imageIds found');
            return false;
          }

          // Patch the INSTANCE's _init to inject imageIds every time it's called.
          // Prototype patches don't work because the instance already has its own methods.
          const instance = group.getToolInstance?.('OverlayGrid');
          if (instance) {
            if (!instance._initPatched) {
              const origInit = instance._init.bind(instance);
              instance._init = function(/*...args*/) {
                if (!this.configuration) this.configuration = {};
                this.configuration.sourceImageIds = imageIds;
                try { return origInit(); } catch(e) { /* safe */ }
              };
              instance._initPatched = true;
            }
            // Also set configuration directly
            if (!instance.configuration) instance.configuration = {};
            instance.configuration.sourceImageIds = imageIds;
          }

          try {
            group.setToolEnabled(toolName);
          } catch (gridErr) {
            console.log('[Tools] OverlayGrid setToolEnabled fallback:', gridErr.message);
          }
          this.#enabledDisplayTools.add(toolName);
          log.debug(`[Tools] Display tool ENABLED: ${toolName}`);
        } else {
          group.setToolEnabled(toolName);
          this.#enabledDisplayTools.add(toolName);
          log.debug(`[Tools] Display tool ENABLED: ${toolName}`);
        }
      }

      // Also apply to 3D group if applicable
      if (this.#toolGroup3D) {
        try {
          if (wasEnabled) this.#toolGroup3D.setToolDisabled(toolName);
          else this.#toolGroup3D.setToolEnabled(toolName);
        } catch (e) { log.debug(`[Tools] 3D display toggle skip ${toolName}:`, e.message); }
      }

      // Emit event so UI can update toggle state
      busEmit(EVENTS.TOOL_CHANGED, {
        toolName,
        previousTool: this.#activeToolName,
        source: 'display-toggle',
        displayEnabled: !wasEnabled
      });

      // Re-render viewports to show/hide overlay
      try {
        const re = _getRE();
        if (re) re.renderViewports(re.getViewports().map(v => v.id));
      } catch (e) { log.debug('[Tools] render after toggle:', e.message); }

      return !wasEnabled;
    } catch (e) {
      log.warn(`[Tools] toggleDisplayTool "${toolName}" failed:`, e.message);
      return false;
    }
  }

  /**
   * Get imageIds for OverlayGrid from viewports or volume cache.
   * @returns {string[]|null}
   */
  #getOverlayGridImageIds() {
    try {
      const re = _getRE();
      if (!re) return null;
      for (const vp of re.getViewports()) {
        try { const ids = vp.getImageIds?.(); if (ids?.length) return ids; } catch(e) {}
        try {
          const actors = vp.getActors?.();
          if (actors?.length) {
            const vid = actors[0]?.uid || actors[0]?.referenceId;
            if (vid) {
              const vol = _cornerstone?.cache?.getVolume?.(vid);
              if (vol?.imageIds?.length) return vol.imageIds;
            }
          }
        } catch(e) {}
      }
    } catch(e) {}
    return null;
  }

  /**
   * Check if a display tool is currently enabled
   * @param {string} toolName
   * @returns {boolean}
   */
  isDisplayToolEnabled(toolName) {
    return this.#enabledDisplayTools.has(toolName);
  }

  // ══════════════════════════════════════════════════════════════
  // [FIX #12] SEGMENTATION — DEFERRED CREATION
  // ══════════════════════════════════════════════════════════════

  #segmentationCreated = false;
  /** [11.0.2] Идентификатор действующей сегментации — адресат для пульта в сайдбаре. */
  #activeSegId = null;

  /**
   * Try to create a segmentation labelmap. If successful, the user can
   * click the segmentation tool button again to activate it.
   * @param {string} toolName — the segmentation tool that was requested
   */
  /**
   * [11.1.26] Возвращает Promise<boolean>: готов ли холст. Раньше функция
   * была «выстрелил и забыл» — вызывающий не мог узнать ни об успехе, ни о
   * причине отказа, и кисть молча не включалась.
   */
  #tryCreateSegmentation(toolName) {
    try {
      const re = _getRE();
      if (!re) return Promise.resolve(false);
      
      // [11.1.36] ID ТОМА СВЕРЯЕТСЯ С КЕШЕМ, а не берётся на веру. Прошлый
      // код брал actor.uid и получал ГОЛЫЙ uuid без схемы загрузчика —
      // журнал владельца показал это дословно: «Referenced volume with id
      // f22efdf4-… does not exist». В кеше том лежит как
      // «cornerstoneStreamingImageVolume:f22efdf4-…» (проект сам всюду
      // клеит/снимает этот префикс — viewport.js:67,1239,1367). Теперь
      // каждый кандидат проверяется по кешу; голому uuid примеряется
      // штатный префикс, а напоследок кеш обыскивается по хвосту id —
      // это работает и для nifti, и для любых будущих схем.
      const кэш = _cornerstone?.cache;
      const вКеше = (id) => { try { return !!(id && кэш?.getVolume?.(id)); } catch (eк) { return false; } };
      const датьПолныйId = (сырой) => {
        if (!сырой) return null;
        const s = String(сырой);
        if (вКеше(s)) return s;
        if (!s.includes(':') && вКеше('cornerstoneStreamingImageVolume:' + s)) {
          return 'cornerstoneStreamingImageVolume:' + s;
        }
        try {
          const найден = (кэш?.getVolumes?.() || []).find((v) => String(v?.volumeId || '').endsWith(s));
          if (найден?.volumeId) return найден.volumeId;
        } catch (eк) { /* кеш недоступен — кандидат мимо */ }
        return null;
      };

      const viewports = re.getViewports?.() || [];
      let volumeId = null;
      let hasVolumeViewport = false;
      const виденные = new Set();

      for (let i = 0; i < viewports.length; i++) {
        try {
          const vp = viewports[i];
          if (!vp || vp.type !== 'orthographic') continue;
          hasVolumeViewport = true;
          const first = vp.getActors?.()?.[0];
          // referencedId — настоящее имя поля в v5 (сверено с BaseVolumeViewport.getVolumeId);
          // referenceId оставлен кандидатом на случай старых сборок.
          const кандидаты = [vp.getVolumeId?.(), first?.referencedId, first?.uid, first?.referenceId, first?.volumeId];
          for (const канд of кандидаты) {
            if (канд) виденные.add(String(канд));
            const полный = датьПолныйId(канд);
            if (полный) { volumeId = полный; break; }
          }
          if (volumeId) break;
        } catch (innerErr) {
          continue;   // это окно не читается — пробуем следующее
        }
      }

      if (!hasVolumeViewport || !volumeId) {
        log.warn(`[Tools] Кисть: том не найден в кеше (окнаMPR=${hasVolumeViewport}; кандидаты: ${[...виденные].join(', ') || 'нет'})`);
        return Promise.resolve(false);
      }
      log.info(`[Tools] Кисть: источник холста — «${volumeId}»`);

      const segId = `mikhmel-seg-${Date.now()}`;
      // [11.1.33] ВОТ ПОЧЕМУ КИСТЬ НЕ РИСОВАЛА. Маска создавалась (это шаг
      // выше и он работал), а вот ПРИВЯЗЫВАЛАСЬ она в никуда:
      // addSegmentationRepresentations в Cornerstone 3D v5 принимает ПЕРВЫМ
      // АРГУМЕНТОМ ИДЕНТИФИКАТОР ОКНА (viewportId), а мы передавали имя
      // ГРУППЫ ИНСТРУМЕНТОВ (TOOL_GROUP_ID) — так было в API v1, где
      // представления цеплялись к группе. Библиотека не ругалась: она честно
      // пыталась привязать маску к «окну» с именем группы, которого нет.
      //
      // [11.1.35] НО КОД 11.1.33 НИ РАЗУ НЕ ВЫПОЛНИЛСЯ — и вот почему кисть
      // «не работала» и после него. Два тихих провала СТРОКОЙ ВЫШЕ:
      //
      // 1) `addSegmentations(...)` в Cornerstone 5.6.3 возвращает VOID, а не
      //    Promise (сверено с исходником библиотеки:
      //    stateManagement/segmentation/addSegmentations.ts, `): void {`).
      //    Прежний код звал на результате `.then()` → мгновенный TypeError
      //    «Cannot read properties of undefined» → его глотал внешний catch
      //    → «tryCreateSegmentation skipped» уровнем debug → холст НЕ
      //    создавался НИКОГДА. setActiveTool('Brush') вечно возвращал false,
      //    левая кнопка оставалась на прежнем инструменте: в объёмном окне —
      //    TrackballRotate («вращает объём»), в срезовых — прокрутка. Оба
      //    симптома владельца — из этой одной строки.
      //
      // 2) Холстом передавался volumeId САМОГО КТ. По контракту библиотеки
      //    (официальный пример labelmapSegmentationTools и наш же рабочий
      //    Seg3D в clinicalTools.js:1105) labelmap — это ПРОИЗВОДНЫЙ пустой
      //    том: createAndCacheDerivedLabelmapVolume(источник, {volumeId}).
      //    Без него мазки писались бы прямо в данные КТ — невидимо в любом
      //    окне W/L. Даже почини мы п.1 — мазка всё равно бы «не было».
      //
      // Маска цепляется к срезовым (orthographic) окнам поимённо: объёмное
      // 3D-окно кистью не рисует по устройству — там всегда вращение.
      const segVolumeId = `${segId}-labelmap`;
      const окнаДляМаски = [];
      for (const vp of (re.getViewports?.() || [])) {
        if (vp?.type === 'orthographic') окнаДляМаски.push(vp.id);
      }
      if (!окнаДляМаски.length) {
        log.warn('[Tools] Кисть: срезовых (MPR) окон в раскладке нет — холсту негде лечь');
        return Promise.resolve(false);
      }
      const создатьХолст = async () => {
        // Производный пустой том той же геометрии — это и есть холст.
        await _cornerstone.volumeLoader.createAndCacheDerivedLabelmapVolume(volumeId, {
          volumeId: segVolumeId,
        });
        // ВНИМАНИЕ: возвращает void — НЕ вешать на результат .then (см. надгробие).
        segmentation.addSegmentations([{
          segmentationId: segId,
          representation: {
            type: cornerstoneTools.Enums.SegmentationRepresentations.Labelmap,
            data: { volumeId: segVolumeId },
          },
        }]);
        const Labelmap = cornerstoneTools.Enums.SegmentationRepresentations.Labelmap;
        const кОкну = segmentation.addLabelmapRepresentationToViewport
          ? (vpId) => segmentation.addLabelmapRepresentationToViewport(vpId, [{ segmentationId: segId }])
          : (vpId) => segmentation.addSegmentationRepresentations(vpId, [{ segmentationId: segId, type: Labelmap }]);
        let привязано = 0;
        for (const vpId of окнаДляМаски) {
          try { await кОкну(vpId); привязано++; }
          catch (e) { log.debug(`[Tools] маска не легла в ${vpId}:`, e.message); }
        }
        // Явной строкой в журнал — по требованию передачи дел: по ней видно,
        // дошла ли маска до окон, без гаданий по молчанию.
        log.warn(`[Tools] Кисть: холст «${segVolumeId}» по тому «${volumeId}», маска в окнах: ${привязано} из ${окнаДляМаски.length} (${окнаДляМаски.join(', ')})`);
        if (!привязано) throw new Error('маску не удалось привязать ни к одному окну');
        return привязано;
      };
      return создатьХолст().then(() => {
        this.#segmentationCreated = true;
        // [11.0.2] Идентификатор ЗАПОМИНАЕТСЯ. Раньше он жил только внутри
        // этой функции как местная переменная и терялся сразу после создания —
        // поэтому пульт сегментации в правом сайдбаре не мог обратиться ни к
        // активному сегменту, ни к прозрачности маски: адресата не было.
        this.#activeSegId = segId;
        log.debug(`[Tools] Segmentation ready: ${segId}`);
        return true;
      }).catch(e => {
        // [11.1.35] warn, не debug: прошлый debug и спрятал TypeError на
        // `.then` от void — три круга переписки искали то, что журнал знал.
        log.warn('[Tools] Кисть: холст не создался —', e.message);
        return false;
      });
    } catch (e) {
      log.warn('[Tools] Кисть: подготовка холста прервана —', e.message);
      return Promise.resolve(false);
    }
  }

  /**
   * [CORE] Применяет состояние инструментов к группе.
   *
   * [FIX #3] Аннотации и сегментации → setToolDisabled (вместо Passive).
   * Passive в Cornerstone3D v2 = инструмент ВСЁ ЕЩЁ перехватывает touch/click
   * на существующих аннотациях, что приводит к конфликтам на мобильных.
   * Disabled = полностью неактивен, не перехватывает ничего.
   *
   * Манипуляции (Pan, Zoom, StackScroll) → Passive (безопасно, не перехватывают).
   */
  #applyToolState(group, newPrimaryTool) {
    const { MouseBindings } = ToolEnums;
    const isAnnotation = (name) =>
      ['annotation', 'segmentation'].includes(ToolManager.REGISTRY[name]?.category);
    // [FIX] Crosshairs в Passive вызывает mouseMoveCallback на каждое движение мыши,
    // что падает с "Cannot read properties of undefined (reading 'length')"
    // если viewports ещё не привязаны. Поэтому Crosshairs → Disabled, как аннотации.
    const mustDisable = (name) =>
      isAnnotation(name) || name === 'Crosshairs';

    // 1. Полный сброс: Disabled для аннотаций + Crosshairs, Passive для манипуляций
    // [FIX #11] Preserve enabled display overlay tools (Grid, Scale, etc.)
    Object.keys(group.toolOptions || {}).forEach(name => {
      try {
        if (name === newPrimaryTool) return;
        // [FIX #11] Don't touch display overlay tools that are currently toggled ON
        if (DISPLAY_TOGGLE_TOOLS.has(name) && this.#enabledDisplayTools.has(name)) return;
        // [FIX #3] Disabled предотвращает перехват touch на аннотациях
        // [FIX #10] Crosshairs Disabled предотвращает спам ошибок mouseMoveCallback
        mustDisable(name) ? group.setToolDisabled(name) : group.setToolPassive(name);
      } catch (e) {
        log.debug(`[Tools] Reset ${name} failed:`, e.message);
      }
    });

    // 2. Восстанавливаем БАЗОВЫЕ инструменты на их фиксированные места
    try {
      // StackScroll -> Только Колесо мыши (на мобильном Wheel не существует — это нормально)
      if (MouseBindings?.Wheel !== undefined) {
        group.setToolActive('StackScroll', { bindings: [{ mouseButton: MouseBindings.Wheel }] });
      }

      // Zoom -> Правая кнопка мыши / 2 Пальца (Pinch)
      group.setToolActive('Zoom', {
        bindings: [
          { mouseButton: MouseBindings.Secondary },
          { numTouchPointers: 2 }
        ]
      });

      // Pan -> Средняя кнопка мыши / 3 Пальца
      group.setToolActive('Pan', {
        bindings: [
          { mouseButton: MouseBindings.Auxiliary },
          { numTouchPointers: 3 }
        ]
      });

      // В 3D: TrackballRotate на левую кнопку, если он не выбран как основной
      if (group === this.#toolGroup3D && newPrimaryTool !== 'TrackballRotate') {
        group.setToolActive('TrackballRotate', {
          bindings: [
            { mouseButton: MouseBindings.Primary },
            { numTouchPointers: 1 }
          ]
        });
      }
    } catch (e) {
      log.warn('[Tools] Failed to restore base tools:', e.message);
    }

    // 3. Активируем ГЛАВНЫЙ инструмент на Primary + 1 палец
    try {
      const primaryBindings = [
        { mouseButton: MouseBindings.Primary },
        { numTouchPointers: 1 }
      ];

      if (newPrimaryTool === 'Pan') {
        // Pan: Primary + 1 палец + Auxiliary (средняя кнопка на десктопе)
        group.setToolActive('Pan', {
          bindings: [...primaryBindings, { mouseButton: MouseBindings.Auxiliary }, { numTouchPointers: 3 }]
        });
      } else if (newPrimaryTool === 'Zoom') {
        // Zoom: Primary + 1 палец + Secondary + 2 пальца
        group.setToolActive('Zoom', {
          bindings: [...primaryBindings, { mouseButton: MouseBindings.Secondary }, { numTouchPointers: 2 }]
        });
      } else if (newPrimaryTool === 'StackScroll') {
        const bindings = [...primaryBindings];
        if (MouseBindings?.Wheel !== undefined) {
          bindings.push({ mouseButton: MouseBindings.Wheel });
        }
        group.setToolActive('StackScroll', { bindings });
      } else if (newPrimaryTool === 'TrackballRotate' && group === this.#toolGroup3D) {
        group.setToolActive('TrackballRotate', { bindings: primaryBindings });
      } else {
        // Обычный инструмент (WindowLevel, Length, Angle и т.д.)
        // [FIX #3] Для annotation tools: сначала Enabled, потом Active
        if (isAnnotation(newPrimaryTool)) {
          group.setToolEnabled(newPrimaryTool);
        }
        
        // [FIX #10] Crosshairs: проверяем что viewports имеют volumes.
        // Без volumes — Crosshairs ставится Active, но mouseMoveCallback 
        // падает на каждый mousemove (Cannot read 'length' of undefined).
        // Cornerstone НЕ бросает исключение — он лишь пишет console.warn,
        // поэтому catch не срабатывает. Нужна явная проверка.
        if (newPrimaryTool === 'Crosshairs') {
          const viewportIds = group.getViewportIds?.() || [];
          let readyCount = 0;
          try {
            const re = _getRE();
            if (re) {
              for (const vpId of viewportIds) {
                const vp = re.getViewport?.(vpId);
                // [6.7.33] getDefaultActor есть не во всех версиях Cornerstone —
                // подстраховываемся списком актёров.
                const hasActor = !!vp?.getDefaultActor?.() || (vp?.getActors?.()?.length > 0);
                if (hasActor) readyCount++;
              }
            }
          } catch (_) { /* safe */ }
          
          if (readyCount < 2) {
            // [6.7.33] Раньше подмена была молчаливой: пользователь жал
            // «Перекрестие», получал прокрутку срезов и не понимал, почему.
            // Теперь причина проговаривается вслух.
            log.warn(`[Tools] ⚠️ Crosshairs blocked: only ${readyCount}/${viewportIds.length} ` +
                     `viewports have volumes (${viewportIds.join(', ')}). Using StackScroll.`);
            showToastSafe(`✚ Перекрестья недоступны: объём загружен только в ${readyCount} окне(ах) из ` +
                          `${viewportIds.length}. Дождитесь загрузки серии.`);
            group.setToolDisabled('Crosshairs');
            group.setToolActive('StackScroll', { bindings: primaryBindings });
            return; // выход из #applyToolState
          }
        }
        
        group.setToolActive(newPrimaryTool, { bindings: primaryBindings });
      }

      log.debug(`[Tools] Primary tool applied: ${newPrimaryTool}`);
    } catch (e) {
      log.warn(`[Tools] Failed to activate primary tool ${newPrimaryTool}:`, e.message);
    }
  }

  // ══════════════════════════════════════════════════════════════
  // VIEWPORT & DEVICE DETECTION
  // ══════════════════════════════════════════════════════════════

  /**
   * [FIX #2] Надёжная детекция touch-устройства.
   * Исключает десктопы с тачскрином (Surface Pro, ноутбуки с сенсорным экраном).
   * Настоящий смартфон/планшет: pointer: coarse + отсутствие hover.
   */
  #detectTouchDevice() {
    const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
    const hasNoHover = window.matchMedia('(hover: none)').matches;
    const hasTouchEvents = 'ontouchstart' in window;
    const hasTouchPoints = navigator.maxTouchPoints > 0;
    const isMobile = (hasCoarsePointer && hasNoHover) || (hasTouchEvents && hasNoHover);
    const isSmallScreen = window.innerWidth <= 1024;

    this.#isTouchDevice = isMobile || (hasTouchPoints && isSmallScreen && hasCoarsePointer);

    log.debug(
      `[Tools] Touch detection: coarse=${hasCoarsePointer}, noHover=${hasNoHover}, ` +
      `touch=${hasTouchEvents}, points=${hasTouchPoints}, small=${isSmallScreen} → isMobile=${this.#isTouchDevice}`
    );

    return this.#isTouchDevice;
  }

  /**
   * [FIX #1] Принудительная блокировка нативного скролла/зума браузером.
   * Cornerstone3D требует touch-action:none на viewport-элементах,
   * иначе мобильный браузер перехватывает жесты (scroll, pinch-zoom, pan).
   */
  #applyTouchActionCSS(viewportIds) {
    try {
      for (const vpId of viewportIds) {
        const el = document.getElementById(vpId) || document.getElementById('viewport-' + vpId);
        if (!el) continue;
        el.style.touchAction = 'none';
        el.style.userSelect = 'none';
        el.style.setProperty('-webkit-user-select', 'none');
        el.querySelectorAll('canvas').forEach(c => {
          c.style.touchAction = 'none';
          c.style.userSelect = 'none';
        });
      }
      // Fallback: контейнер может иметь data-атрибут или стандартный ID
      const container =
        document.querySelector(`[data-rendering-engine-id="${this.#config.renderingEngineId}"]`) ||
        document.getElementById('viewport-container');
      if (container) container.style.touchAction = 'none';

      log.debug('[Tools] touch-action:none applied to', viewportIds.length, 'viewports');
    } catch (e) {
      log.warn('[Tools] Failed to apply touch-action CSS:', e.message);
    }
  }

  /**
   * Подсчёт viewport'ов в группе (совместимость с CS3D v1 и v2).
   */
  #getViewportCount(group) {
    if (!group) return 0;
    if (group.viewportsInfo && Array.isArray(group.viewportsInfo)) return group.viewportsInfo.length;
    if (group._viewports) return Object.keys(group._viewports).length;
    if (typeof group.getViewportIds === 'function') {
      try { return group.getViewportIds().length; }
      catch (e) { log.debug('[Tools] getViewportCount fallback:', e.message); return 0; }
    }
    return 0;
  }

  /**
   * Проверяет, можно ли активировать инструмент с учётом кол-ва viewport'ов.
   * Если нельзя — возвращает fallback-имя, иначе null.
   */
  #checkViewportRequirement(toolName, group) {
    const required = ToolManager.REGISTRY[toolName]?.minViewports;
    if (!required) return null; // нет ограничений

    const count = this.#getViewportCount(group);
    if (count >= required) return null; // достаточно viewport'ов

    log.warn(
      `[Tools] ⚠️ "${toolName}" requires ≥${required} viewports, but group has ${count}. ` +
      `Using "${this.#config.fallbackTool}" instead. Activate manually via toolbar (X) when MPR is ready.`
    );
    return this.#config.fallbackTool;
  }

  /**
   * Добавляет viewport в 2D tool group.
   * Используйте при динамическом добавлении MPR-viewport'ов.
   * [FIX #9] НЕ вызывает retryPendingTool — Crosshairs не включится автоматически.
   */
  addViewportToGroup(viewportId, renderingEngineId = this.#config.renderingEngineId) {
    if (!this.#toolGroup2D) {
      log.warn('[Tools] addViewportToGroup: tool group not initialized');
      return;
    }
    try {
      this.#toolGroup2D.addViewport(viewportId, renderingEngineId);
      this.#applyTouchActionCSS([viewportId]);
      log.debug(`[Tools] Viewport "${viewportId}" added to 2D group (total: ${this.#getViewportCount(this.#toolGroup2D)})`);
    } catch (e) {
      log.warn(`[Tools] Failed to add viewport "${viewportId}":`, e.message);
    }
  }

  /**
   * Повторная попытка активации отложенного инструмента.
   * Экспортирован для совместимости, но #pendingTool всегда null
   * благодаря FIX #9, поэтому всегда возвращает false.
   */
  retryPendingTool() {
    if (!this.#pendingTool) return false;

    const toolName = this.#pendingTool;

    // Проверяем, хватает ли теперь viewport'ов
    if (this.#toolGroup2D) {
      const fallback = this.#checkViewportRequirement(toolName, this.#toolGroup2D);
      if (fallback) {
        log.debug(`[Tools] retryPendingTool: "${toolName}" still needs more viewports`);
        return false;
      }
    }

    log.debug(`[Tools] retryPendingTool: activating deferred "${toolName}"`);
    this.#pendingTool = null;
    return this.setActiveTool(toolName);
  }

  /**
   * Принудительное обновление touch-action CSS.
   * Вызывайте после динамических изменений DOM (resize, layout switch).
   */
  refreshTouchAction(viewportIds, renderingEngineId = this.#config.renderingEngineId) {
    this.#config.renderingEngineId = renderingEngineId;
    this.#applyTouchActionCSS(viewportIds);
  }

  // ══════════════════════════════════════════════════════════════
  // ANNOTATION MANAGEMENT
  // ══════════════════════════════════════════════════════════════

  /** Получить аннотации (для viewport'а или все) */
  getAnnotations(viewportId) {
    if (viewportId) {
      try { return annotation.state.getAnnotations(viewportId) || []; }
      catch (e) { log.warn(`[Tools] getAnnotations failed for viewport ${viewportId}:`, e.message); return []; }
    }
    return annotation.state.getAllAnnotations();
  }

  removeAnnotation(annotationUID) {
    annotation.state.removeAnnotation(annotationUID);
  }

  clearAnnotations() {
    annotation.state.removeAllAnnotations();
  }

  /** Экспорт аннотаций в JSON строку */
  exportAnnotations() {
    return JSON.stringify(annotation.state.getAllAnnotations(), null, 2);
  }

  /**
   * Импорт аннотаций из JSON строки или массива.
   * Пропускает невалидные записи (без UID или metadata).
   */
  importAnnotations(json) {
    let data;
    try {
      data = typeof json === 'string' ? JSON.parse(json) : json;
    } catch (e) {
      log.warn('[Tools] importAnnotations: invalid JSON', e.message);
      return;
    }
    if (!Array.isArray(data)) {
      log.warn('[Tools] importAnnotations: expected array, got', typeof data);
      return;
    }

    let imported = 0;
    for (const ann of data) {
      if (ann?.annotationUID && ann?.metadata) {
        try {
          annotation.state.addAnnotation(ann);
          imported++;
        } catch (e) {
          log.warn(`[Tools] Skip invalid annotation ${ann.annotationUID}:`, e.message);
        }
      } else {
        log.debug('[Tools] Skip annotation without UID/metadata');
      }
    }
    log.debug(`[Tools] Imported ${imported}/${data.length} annotations`);
  }

  // ══════════════════════════════════════════════════════════════
  // SEGMENTATION MANAGEMENT
  // ══════════════════════════════════════════════════════════════

  /**
   * Создать сегментацию (Labelmap) по ГОТОВОМУ labelmap-тому.
   *
   * [11.1.35] Параметр переименован из volumeId в labelmapVolumeId — прежнее
   * имя приглашало в ту же яму, что сломала кисть: сюда НЕЛЬЗЯ передавать
   * том самого КТ. Холст — производный пустой том
   * (createAndCacheDerivedLabelmapVolume), и создать его обязан вызывающий.
   * Вызывающих у функции сейчас ноль (экспорт наружу «на вырост»); await на
   * void-addSegmentations безвреден — это не `.then`, которым болел
   * #tryCreateSegmentation.
   * @param {string} segmentationId
   * @param {string} labelmapVolumeId — id ПРОИЗВОДНОГО пустого тома-холста
   */
  async createSegmentation(segmentationId, labelmapVolumeId) {
    try {
      await segmentation.addSegmentations([{
        segmentationId,
        representation: {
          type: cornerstoneTools.Enums.SegmentationRepresentations.Labelmap,
          data: { volumeId: labelmapVolumeId }
        },
      }]);
      return segmentationId;
    } catch (e) {
      log.error(`[Tools] Failed to create segmentation ${segmentationId}:`, e.message);
      throw e;
    }
  }

  setActiveSegment(segmentationId, segmentIndex) {
    try {
      segmentation.activeSegmentation.setActiveSegmentIndex(segmentationId, segmentIndex);
    } catch (e) {
      log.warn(`[Tools] Failed to set active segment ${segmentIndex} for ${segmentationId}:`, e.message);
    }
  }

  getSegmentationData(segmentationId) {
    try {
      return segmentation.state.getSegmentation(segmentationId);
    } catch (e) {
      log.warn(`[Tools] Failed to get segmentation data for ${segmentationId}:`, e.message);
      return null;
    }
  }

  setBrushSize(radius) {
    try {
      cornerstoneTools.utilities.segmentation.setBrushSizeForToolGroup(TOOL_GROUP_ID, radius);
    } catch (e) {
      log.warn('[Tools] Failed to set brush size:', e.message);
    }
  }

  /** [11.0.2] Идентификатор действующей сегментации (null, пока кисти не касались). */
  getActiveSegmentationId() { return this.#activeSegId; }

  /**
   * [11.0.2] Прозрачность заливки маски, 0…1.
   *
   * Ползунок «Прозрачность» на вкладке сегментации менял только подпись рядом
   * с собой: до маски значение не доходило, потому что звать было некого —
   * этой функции не существовало.
   *
   * Имя поля взято не по памяти, а из типов установленной библиотеки
   * (@cornerstonejs/tools 5.6.3, types/LabelmapTypes.d.ts): у стиля Labelmap
   * прозрачность заливки называется `fillAlpha`. Ставим её слитно с
   * `fillAlphaInactive`, иначе неактивные сегменты остались бы прежними и
   * ползунок выглядел бы работающим наполовину.
   */
  setSegmentationOpacity(alpha) {
    const a = Math.max(0, Math.min(1, Number(alpha)));
    const segId = this.#activeSegId;
    if (!segId) return false;
    try {
      const тип = cornerstoneTools.Enums.SegmentationRepresentations.Labelmap;
      segmentation.config.style.setStyle(
        { segmentationId: segId, type: тип },
        { fillAlpha: a, fillAlphaInactive: Math.max(0, a - 0.2) },
        true,
      );
      return true;
    } catch (e) {
      log.warn('[Tools] Failed to set segmentation opacity:', e.message);
      return false;
    }
  }

  // ══════════════════════════════════════════════════════════════
  // [v8] ENTERPRISE: Diagnostics, Config, Batch, Presets
  // ══════════════════════════════════════════════════════════════

  /**
   * [v8] Полная диагностика состояния ToolManager
   */
  getDiagnostics() {
    const reg = ToolManager.REGISTRY;
    const categories = {};
    for (const [name, e] of Object.entries(reg)) {
      categories[e.category] = (categories[e.category] || 0) + 1;
    }

    let toolStates2D = {};
    if (this.#toolGroup2D) {
      try {
        for (const name of Object.keys(reg)) {
          try {
            const opts = this.#toolGroup2D.toolOptions?.[name];
            toolStates2D[name] = opts?.mode || 'unknown';
          } catch { toolStates2D[name] = 'error'; }
        }
      } catch (e) { log.debug("[toolManager]", e.message); }
    }

    return {
      activeTool: this.#activeToolName,
      pendingTool: this.#pendingTool,
      isTouchDevice: this.#isTouchDevice,
      has2DGroup: !!this.#toolGroup2D,
      has3DGroup: !!this.#toolGroup3D,
      viewportCount2D: this.#getViewportCount(this.#toolGroup2D),
      viewportCount3D: this.#getViewportCount(this.#toolGroup3D),
      totalRegisteredTools: Object.keys(reg).length,
      categories,
      toolStates2D,
      config: { ...this.#config },
    };
  }

  /**
   * [v8] Получить информацию о конкретном инструменте
   */
  getToolInfo(toolName) {
    const entry = ToolManager.REGISTRY[toolName];
    if (!entry) return null;
    return {
      name: toolName,
      category: entry.category,
      icon: entry.icon,
      shortcut: entry.shortcut,
      minViewports: entry.minViewports || 1,
      isAvailable: !!entry.tool,
      isActive: this.#activeToolName === toolName,
    };
  }

  /**
   * [v8] Переключить между двумя инструментами (toggle)
   * Если текущий = toolName → переключаемся на fallback (StackScroll)
   */
  toggleTool(toolName) {
    if (this.#activeToolName === toolName) {
      return this.setActiveTool(this.#config.fallbackTool);
    }
    return this.setActiveTool(toolName);
  }

  /**
   * [v8] Быстрые пресеты (наборы инструментов)
   */
  applyToolPreset(preset) {
    const presets = {
      'measurement': 'Length',
      'navigation': 'StackScroll',
      'annotation': 'ArrowAnnotate',
      'freehand': 'PlanarFreehandROI',
      'windowing': 'WindowLevel',
      'zoom': 'Zoom',
      'pan': 'Pan',
      'rotation': 'TrackballRotate',
      'crosshairs': 'Crosshairs',
      'segmentation': 'Brush',
      'eraser': 'Eraser',
    };
    const tool = presets[preset];
    if (!tool) {
      return { success: false, error: `Unknown preset: "${preset}". Available: ${Object.keys(presets).join(', ')}` };
    }
    const ok = this.setActiveTool(tool);
    return { success: ok, tool, preset };
  }

  /**
   * [v8] Список пресетов
   */
  listToolPresets() {
    return ['measurement', 'navigation', 'annotation', 'freehand', 'windowing', 'zoom', 'pan', 'rotation', 'crosshairs', 'segmentation', 'eraser'];
  }

  /**
   * [v8] Подсчёт аннотаций по типам
   */
  getAnnotationStats() {
    try {
      const all = annotation.state.getAllAnnotations();
      const byTool = {};
      let total = 0;
      if (Array.isArray(all)) {
        for (const ann of all) {
          const tool = ann?.metadata?.toolName || 'unknown';
          byTool[tool] = (byTool[tool] || 0) + 1;
          total++;
        }
      } else if (typeof all === 'object') {
        // CS3D v2: object keyed by frameOfReferenceUID
        for (const frameAnnotations of Object.values(all)) {
          for (const toolGroup of Object.values(frameAnnotations)) {
            if (Array.isArray(toolGroup)) {
              for (const ann of toolGroup) {
                const tool = ann?.metadata?.toolName || 'unknown';
                byTool[tool] = (byTool[tool] || 0) + 1;
                total++;
              }
            }
          }
        }
      }
      return { total, byTool };
    } catch (e) {
      return { total: 0, byTool: {}, error: e.message };
    }
  }

  /**
   * [v8] Установить fallback tool
   */
  setFallbackTool(toolName) {
    if (!ToolManager.REGISTRY[toolName]) throw new Error(`Unknown tool: ${toolName}`);
    this.#config.fallbackTool = toolName;
  }

  /**
   * [v8] Получить текущий fallback
   */
  getFallbackTool() { return this.#config.fallbackTool; }

  // ══════════════════════════════════════════════════════════════
  // GETTERS & CLEANUP
  // ══════════════════════════════════════════════════════════════

  get activeTool() { return this.#activeToolName; }
  get pendingTool() { return this.#pendingTool; }
  get isTouchDevice() { return this.#isTouchDevice; }
  get toolGroup() { return this.#toolGroup2D; }

  /** Получить инструменты по категории ('annotation', 'manipulation', 'segmentation', 'display') */
  getToolsByCategory(category) {
    return Object.entries(ToolManager.REGISTRY)
      .filter(([_, e]) => e.category === category)
      .map(([name, e]) => ({ name, ...e }));
  }

  getAllToolNames() { return Object.keys(ToolManager.REGISTRY); }
  getToolRegistry() { return { ...ToolManager.REGISTRY }; }

  /** Карта горячих клавиш: { 'L': 'Length', 'W': 'WindowLevel', ... } */
  getShortcutMap() {
    const map = {};
    for (const [name, entry] of Object.entries(ToolManager.REGISTRY)) {
      if (entry.shortcut) map[entry.shortcut] = name;
    }
    return map;
  }

  /**
   * Полная очистка: уничтожение tool groups, аннотаций, обнуление состояния.
   * Вызывается при закрытии исследования.
   */
  destroyTools() {
    try {
      if (this.#toolGroup2D) ToolGroupManager.destroyToolGroup(TOOL_GROUP_ID);
      if (this.#toolGroup3D) ToolGroupManager.destroyToolGroup(TOOL_GROUP_3D_ID);
    } catch (e) {
      log.warn('[Tools] ToolGroup cleanup failed:', e.message);
    }

    try {
      annotation.state.removeAllAnnotations();
    } catch (e) {
      log.debug('[Tools] Annotation cleanup:', e.message);
    }

    this.#toolGroup2D = null;
    this.#toolGroup3D = null;
    this.#activeToolName = null;
    this.#pendingTool = null;
    this.#enabledDisplayTools.clear();
    this.#segmentationCreated = false;
    this.#registeredInGroup.clear();

    log.debug('[Tools] Tools destroyed and memory cleared');
  }
}

// ══════════════════════════════════════════════════════════════
// 100% BACKWARDS COMPATIBLE API EXPORTS
// ══════════════════════════════════════════════════════════════
//
// Создаём singleton, но наружу отдаём классические функции.
// ВСЕ 25 именованных экспортов сохранены — ни один import
// в viewport.js, layoutManager.js, uiManager.js, featureBindings.js,
// toolBindings.js, proFeaturesBindings.js НЕ сломается.
//

const _manager = new ToolManager();

// ── Core ──
export function onToolChange(callback) { return _manager.onToolChange(callback); }
export function initTools(viewportIds, renderingEngineId = RENDERING_ENGINE_ID) { return _manager.initTools(viewportIds, renderingEngineId); }
export function activateDataTools() { return _manager.activateDataTools(); }
export function setActiveTool(toolName) { return _manager.setActiveTool(toolName); }
export function getActiveTool() { return _manager.activeTool; }
export function isTouchDevice() { return _manager.isTouchDevice; }

// ── Registry & Shortcuts ──
export function getToolsByCategory(category) { return _manager.getToolsByCategory(category); }
export function getAllToolNames() { return _manager.getAllToolNames(); }
export function getToolRegistry() { return _manager.getToolRegistry(); }
export function getToolGroup() { return _manager.toolGroup; }
export function getShortcutMap() { return _manager.getShortcutMap(); }

// ── Viewport Management ──
export function addViewportToGroup(viewportId, renderingEngineId) { return _manager.addViewportToGroup(viewportId, renderingEngineId); }
export function retryPendingTool() { return _manager.retryPendingTool(); }
export function getPendingTool() { return _manager.pendingTool; }
export function refreshTouchAction(viewportIds, renderingEngineId) { return _manager.refreshTouchAction(viewportIds, renderingEngineId); }

// ── Annotations ──
export function getAnnotations(viewportId) { return _manager.getAnnotations(viewportId); }
export function removeAnnotation(annotationUID) { return _manager.removeAnnotation(annotationUID); }
export function clearAnnotations() { return _manager.clearAnnotations(); }
export function exportAnnotations() { return _manager.exportAnnotations(); }
export function importAnnotations(json) { return _manager.importAnnotations(json); }

// ── Segmentation ──
export async function createSegmentation(segmentationId, volumeId) { return _manager.createSegmentation(segmentationId, volumeId); }
export function setActiveSegment(segmentationId, segmentIndex) { return _manager.setActiveSegment(segmentationId, segmentIndex); }
export function getSegmentationData(segmentationId) { return _manager.getSegmentationData(segmentationId); }
export function setBrushSize(radius) { return _manager.setBrushSize(radius); }
// [11.0.2] Провода к пульту сегментации в правом сайдбаре.
export function getActiveSegmentationId() { return _manager.getActiveSegmentationId(); }
export function setSegmentationOpacity(alpha) { return _manager.setSegmentationOpacity(alpha); }

// ── Cleanup ──
export function destroyTools() { return _manager.destroyTools(); }

// ── [FIX #11] Display Overlay Toggle ──
export function toggleDisplayTool(toolName) { return _manager.toggleDisplayTool(toolName); }
export function isDisplayToolEnabled(toolName) { return _manager.isDisplayToolEnabled(toolName); }
export { DISPLAY_TOGGLE_TOOLS };

// ── [v8] Enterprise ──
export function getDiagnostics() { return _manager.getDiagnostics(); }
export function getToolInfo(toolName) { return _manager.getToolInfo(toolName); }
export function toggleTool(toolName) { return _manager.toggleTool(toolName); }
export function applyToolPreset(preset) { return _manager.applyToolPreset(preset); }
export function listToolPresets() { return _manager.listToolPresets(); }
export function getAnnotationStats() { return _manager.getAnnotationStats(); }
export function setFallbackTool(toolName) { return _manager.setFallbackTool(toolName); }
export function getFallbackTool() { return _manager.getFallbackTool(); }

/**
 * [11.1.21] Стиль аннотаций — провод к движку. Раздел «Стиль аннотаций» на
 * вкладке «Измерения» три года был вывеской: цвет менял только подпись рядом
 * с пипеткой, толщина и шрифт — только цифру (подозрение владельца в
 * комментарии [10.19.32] аудит подтвердил поиском: ни одного вызова
 * config.style во всём src). Теперь настройки уходят в глобальный стиль
 * cornerstoneTools и перерисовывают открытые окна — включая УЖЕ нарисованные
 * измерения.
 * @param {{color?:string, lineWidth?:number|string, fontSize?:number|string}} н
 * @returns {boolean} применилось ли
 */
export function setAnnotationStyle(н = {}) {
  try {
    const стиль = {};
    if (н.color) {
      стиль.color = н.color;
      стиль.colorHighlighted = н.color;
      стиль.colorSelected = н.color;
      стиль.textBoxColor = н.color;
    }
    if (н.lineWidth != null) стиль.lineWidth = String(н.lineWidth);
    if (н.fontSize != null) стиль.textBoxFontSize = `${н.fontSize}px`;
    const конфиг = annotation?.config?.style;
    if (!конфиг?.setGlobalStyle) return false;
    const текущий = конфиг.getGlobalStyle?.() || {};
    конфиг.setGlobalStyle({ ...текущий, ...стиль });
    // Перерисовать открытые окна, чтобы стиль лёг и на уже нарисованное.
    try {
      const re = _getRE(RENDERING_ENGINE_ID);
      re?.renderViewports?.(re.getViewports().map((v) => v.id));
    } catch (e) { /* окон может ещё не быть */ }
    return true;
  } catch (e) {
    return false;
  }
}
